
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

interface FormData {
  startup_name: string;
  bio: string;
  website_url: string;
  category_tags: string[];
  verification_documents: string[];
}

export const useStartupApplicationForm = (onSuccess: () => void, setHasExistingAccount: (value: boolean) => void) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    startup_name: "",
    bio: "",
    website_url: "",
    category_tags: [],
    verification_documents: []
  });

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addCategoryTag = (category: string) => {
    if (!formData.category_tags.includes(category)) {
      setFormData(prev => ({
        ...prev,
        category_tags: [...prev.category_tags, category]
      }));
    }
  };

  const removeCategoryTag = (category: string) => {
    setFormData(prev => ({
      ...prev,
      category_tags: prev.category_tags.filter(tag => tag !== category)
    }));
  };

  const handleSubmit = async (e: React.FormEvent, hasExistingAccount: boolean) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to apply",
        variant: "destructive"
      });
      return;
    }

    if (hasExistingAccount) {
      toast({
        title: "Application Already Exists",
        description: "You already have a startup application submitted",
        variant: "destructive"
      });
      return;
    }

    if (!formData.startup_name.trim() || !formData.bio.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);

      const { error } = await supabase
        .from("startup_accounts")
        .insert({
          user_id: user.id,
          startup_name: formData.startup_name.trim(),
          bio: formData.bio.trim(),
          website_url: formData.website_url.trim() || null,
          category_tags: formData.category_tags,
          verification_documents: formData.verification_documents,
          status: 'pending'
        });

      if (error) {
        console.error("Error submitting application:", error);
        if (error.code === '23505') {
          toast({
            title: "Application Already Exists",
            description: "You already have a startup application submitted",
            variant: "destructive"
          });
          setHasExistingAccount(true);
        } else {
          throw error;
        }
        return;
      }

      toast({
        title: "Application Submitted",
        description: "Your startup application has been submitted for review"
      });

      onSuccess();
    } catch (error) {
      console.error("Error submitting application:", error);
      toast({
        title: "Error",
        description: "Failed to submit application. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    loading,
    handleInputChange,
    addCategoryTag,
    removeCategoryTag,
    handleSubmit
  };
};
